import {
  AccountInfo,
  IPublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { loginRequest } from "../authConfig";

// Entra ID never classifies an expired SPA refresh token (AADSTS700084,
// error "invalid_grant") as an InteractionRequiredAuthError - MSAL only
// does that for "interaction_required" / "consent_required" / "login_required".
// Callers that don't already get an automatic interactive fallback from MSAL
// (e.g. a raw acquireTokenSilent call) need to check for both cases.
export const isInvalidGrantError = (error: unknown): boolean =>
  (error as { errorCode?: string })?.errorCode === "invalid_grant";

export const isReauthRequiredError = (error: unknown): boolean =>
  error instanceof InteractionRequiredAuthError || isInvalidGrantError(error);

const hasSameScopes = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((scope, i) => scope === b[i]);

/**
 * Acquires an access token silently. If silent renewal fails because the
 * session needs re-authentication (expired SPA refresh token, revoked
 * session, blocked silent SSO, etc.), redirects the user through Entra
 * sign-in instead of failing the caller.
 *
 * Only the default (Apollo-facing) scope request is cached to localStorage -
 * a caller asking for a narrower scope (e.g. Sites.Read.All for a single
 * Graph call) must not clobber the token Apollo sends on every GraphQL call.
 */
export async function getAccessToken(
  instance: IPublicClientApplication,
  account: AccountInfo | undefined,
  scopes: string[] = loginRequest.scopes
): Promise<string> {
  const request = { scopes, account };
  const isDefaultScopeRequest = hasSameScopes(scopes, loginRequest.scopes);

  try {
    const response = await instance.acquireTokenSilent(request);
    if (isDefaultScopeRequest && typeof window !== "undefined") {
      localStorage.setItem("token", response.accessToken);
    }
    return response.accessToken;
  } catch (error) {
    if (isReauthRequiredError(error)) {
      try {
        await instance.acquireTokenRedirect(request);
      } catch {
        // Another component may already have an interactive request in
        // flight for the same expired session (interaction_in_progress);
        // that pending redirect will navigate the page away regardless, so
        // this failure isn't something the caller needs to react to.
      }
      // Either the redirect above is now in flight, or another one already
      // was - the caller never gets a token back from this path.
      return "";
    }
    throw error;
  }
}
